import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import type { AbstractControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Observable,
  ReplaySubject,
  catchError,
  combineLatest,
  debounceTime,
  map,
  of,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { SnackbarService } from '@comparit/core';
import { ValidationService } from '@comparit/common-api';
import type {
  PlzOrte,
  PlzResponseDto,
  StrasseSuggestionDto,
  StrassenSearchResponseDto,
  ValidatedAddressDto,
} from '@comparit/common-api';

/** A funnel address. Structural on purpose — apps map their generated Adresse DTOs onto it. */
export interface AddressValues {
  plz: string;
  ort: string;
  strasse?: string;
  hausnummer?: string;
}

export interface AddressValidationResult {
  found: boolean;
  /** Backend spelling of the address, applied to the form when `found`. */
  normalized?: AddressValues;
  suggestions: AddressValues[];
}

const DEBOUNCE_MS = 300;
const COMPLETE_PLZ = /^[0-9]{5}$/;
/** Partial input still yields suggestions, but only digits ever go on the wire. */
const PARTIAL_PLZ = /^[0-9]{2,5}$/;
/** Below this an Ort is still being typed, and would scope the street search to nothing. */
const MIN_ORT_LENGTH = 3;

@Injectable({ providedIn: 'root' })
export class AddressService {
  private validation = inject(ValidationService);
  private destroyRef = inject(DestroyRef);
  private snackbar = inject(SnackbarService);
  // One ref per stream: a street outage must not dismiss the PLZ outage notice, or vice versa.
  private zipSnackbarRef?: number;
  private streetSnackbarRef?: number;
  private validationSnackbarRef?: number;

  readonly isLoadingZip = signal(false);
  readonly isLoadingStreet = signal(false);

  selectIsLoadingZip() {
    return this.isLoadingZip;
  }

  selectIsLoadingStreet() {
    return this.isLoadingStreet;
  }

  selectCitiesByZipChanges(
    zipControl: AbstractControl<string | null>
  ): Observable<PlzOrte[] | undefined> {
    return zipControl.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      startWith(zipControl.getRawValue()),
      debounceTime(DEBOUNCE_MS),
      tap(() => this.isLoadingZip.set(true)),
      // A new PLZ invalidates any street list already on screen.
      tap(() => this.reloadStreetPrefill$.next()),
      tap(() => this.snackbar.close(this.zipSnackbarRef)),
      switchMap((rawZip: string | null) => {
        const plz = trim(rawZip);
        if (!this.isZipValid(plz)) {
          return of({ plz, plzOrte: [] } as PlzResponseDto);
        }
        return this.validation.getPlz({ plz }).pipe(
          catchError(() => {
            this.zipSnackbarRef = this.showErrorSnackbar();
            this.isLoadingZip.set(false);
            return of(undefined);
          })
        );
      }),
      map(result => result?.plzOrte),
      tap(() => this.isLoadingZip.set(false))
    );
  }

  /** Pushed whenever the PLZ changes, so an open street list re-queries against it. */
  readonly reloadStreetPrefill$ = new ReplaySubject<void>(1);

  /**
   * Street suggestions for the typed street, narrowed by the PLZ/Ort entered alongside it.
   * Re-runs on a PLZ change via reloadStreetPrefill$ so an already-open list is never left
   * scoped to the previous town.
   */
  selectAddressByStreetChanges(
    streetControl: AbstractControl<string | null>,
    zipControl?: AbstractControl<string | null>,
    cityControl?: AbstractControl<string | null>
  ): Observable<StrasseSuggestionDto[] | undefined> {
    return combineLatest([
      streetControl.valueChanges.pipe(startWith(streetControl.getRawValue())),
      this.reloadStreetPrefill$.pipe(startWith(undefined)),
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
      map(([street]) => trim(street)),
      withLatestFrom(
        zipControl?.valueChanges.pipe(
          startWith(zipControl.getRawValue()),
          // Only a complete PLZ narrows the search; a half-typed one would drop every hit.
          map(zip => (COMPLETE_PLZ.test(trim(zip)) ? trim(zip) : ''))
        ) ?? of(''),
        cityControl?.valueChanges.pipe(
          startWith(cityControl.getRawValue()),
          map(city => trim(city))
        ) ?? of('')
      ),
      debounceTime(DEBOUNCE_MS),
      tap(() => this.isLoadingStreet.set(true)),
      tap(() => this.snackbar.close(this.streetSnackbarRef)),
      switchMap(([str, plz, ort]) => {
        if (!this.isStreetValid(str)) {
          return of({ eingabe: str, strassen: [] } as StrassenSearchResponseDto);
        }

        return this.validation
          .getStrassen({
            str,
            plz: plz || undefined,
            // A half-typed Ort scopes the search to nothing and looks like "no such street".
            ort: ort.length >= MIN_ORT_LENGTH ? ort : undefined,
          })
          .pipe(
            catchError(() => {
              this.streetSnackbarRef = this.showErrorSnackbar();
              this.isLoadingStreet.set(false);
              return of(undefined);
            })
          );
      }),
      map(result => result?.strassen),
      tap(() => this.isLoadingStreet.set(false))
    );
  }

  getCitiesByZip(plz: string): Observable<PlzOrte[] | null> {
    return this.validation.getPlz({ plz }).pipe(
      map(result => result.plzOrte),
      catchError(() => {
        this.zipSnackbarRef = this.showErrorSnackbar();
        return of(null);
      })
    );
  }

  /**
   * Asks the backend to validate and normalize a complete address. Resolves to `null` when the
   * service is unreachable so a dead address service can never block the funnel.
   */
  validateAddress(address: AddressValues): Observable<AddressValidationResult | null> {
    this.snackbar.close(this.validationSnackbarRef);

    const body = {
      plz: address.plz,
      stadt: address.ort,
      strasse: address.strasse ?? '',
      hausnummer: address.hausnummer ?? '',
    };

    return this.validation.validateAdresse({ validateAdresseRequestDto: body }).pipe(
      map(result => ({
        found: result.found,
        normalized: result.normalized ? toAddressValues(result.normalized) : undefined,
        suggestions: (result.suggestions ?? []).map(toAddressValues),
      })),
      catchError(() => {
        this.validationSnackbarRef = this.showErrorSnackbar();
        return of(null);
      })
    );
  }

  /**
   * Addresses the customer has already settled — either the backend recognised them, or they
   * chose "Fortfahren" on the warning. Keeps a back-and-forth between steps from re-POSTing and
   * from re-opening a dialog they just dismissed.
   */
  private confirmedAddresses = new Set<string>();

  isAddressConfirmed(key: string): boolean {
    return this.confirmedAddresses.has(key);
  }

  markAddressConfirmed(key: string): void {
    this.confirmedAddresses.add(key);
  }

  private isZipValid(zip: string): boolean {
    // Digits only: +'1e3' and +'0x12' are numbers, and the backend answers them with an error
    // that surfaces to the customer as "Adressservice nicht verfügbar".
    return PARTIAL_PLZ.test(zip.trim());
  }

  private isStreetValid(str: string): boolean {
    return str.trim().length >= 2;
  }

  private showErrorSnackbar(): number | undefined {
    return this.snackbar.error(
      'Der Adressservice ist aktuell nicht verfügbar, Du kannst dennoch mit der eingegebenen Adresse fortfahren',
      { disableTimeout: true }
    );
  }
}

function trim(value: string | null | undefined): string {
  return (value ?? '').trim();
}

function toAddressValues(dto: ValidatedAddressDto): AddressValues {
  return {
    plz: dto.plz ?? '',
    ort: dto.stadt ?? '',
    strasse: dto.strasse ?? '',
    hausnummer: dto.hausnummer ?? '',
  };
}
